import { useEffect, useRef, useState } from "react";
import axios from "axios";
import Check from '../assets/images/check.png';
import Remove from '../assets/images/remove.png';
import { useSelector } from "react-redux";

export default function UploadModal({ FilePathUpdate, file, url }) {
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("uploading");

  const AccessToken = useSelector((state)=> state.Token.value);

  const messageRef = useRef('');
  const filePath = useRef(null);

  useEffect(() => {
    handleUpload();
  }, [file]);

  const getChunkSize = (fileSize) => {
    const MB = 1024 * 1024;
    const GB = 1024 * MB;

    if (fileSize < 10 * MB) return null;
    if (fileSize <= 500 * MB) return 10 * MB;
    if (fileSize <= 2 * GB) return 17 * MB;

    return 25 * MB;
  };

  const uploadWithRetry = async (formData, retries = 3) => {
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        let data = await axios.post(url, formData, {
          timeout: 0,
          headers: {
            Authorization: `Bearer ${AccessToken}`,
          },
        });

        filePath.current = data?.data?.file_id ?? null;
        return data;

      } catch (err) {
        const status = err?.response?.status;

        if (attempt < retries && [429, 500, 502, 503, 504].includes(status)) {
          await new Promise((r) => setTimeout(r, 1000 * attempt));
          continue;
        }

        messageRef.current = err?.response?.data?.message ?? '';
        throw err;
      }
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    try {
      setStatus("uploading");

      const chunkSize = getChunkSize(file.size);

      // small file
      if (!chunkSize) {
        const formData = new FormData();
        formData.append("file", file);

        await uploadWithRetry(formData);

        setProgress(100);
        setStatus("success");
        return;
      }

      // big file
      const totalChunks = Math.ceil(file.size / chunkSize);

      for (let i = 0; i < totalChunks; i++) {
        const start = i * chunkSize;
        const end = start + chunkSize;

        const chunk = file.slice(start, end);

        const formData = new FormData();
        formData.append("file", chunk);
        formData.append("chunkIndex", i);
        formData.append("totalChunks", totalChunks);
        formData.append("fileName", file.name);

        await uploadWithRetry(formData);

        setProgress(Math.round(((i + 1) / totalChunks) * 100));
      }

      setStatus("success");

    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          padding: '30px',
          borderRadius: '12px',
          textAlign: 'center',
          minWidth: '220px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
        }}
      >
        <Circle progress={progress} status={status} />

        {status === "success" && (
          <>
            <p className="text-success">File uploaded successfully.</p>
            <button
              type="button"
              className="btn btn-success"
              onClick={(e) => {
                FilePathUpdate(filePath.current);
                e.stopPropagation();
              }}
            >
              Done
            </button>
          </>
        )}

        {status === "error" && (
          <>
            <p className="text-danger" style={{ maxWidth: "170px" }}>
              {messageRef.current}
            </p>
            <button
              type="button"
              className="btn btn-danger"
              onClick={(e) => {
                FilePathUpdate();
                e.stopPropagation();
              }}
            >
              Ok
            </button>
          </>
        )}
      </div>
    </div>
  );
}



function Circle({ progress, status }) {
  const radius = 55;
  const stroke = 8;
  const normalized = radius - stroke * 2;
  const circumference = normalized * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;

  const color =
    status === "success"
      ? "green"
      : status === "error"
      ? "red"
      : "#3b82f6";

  return (
    <div style={{ textAlign: "center" }}>
      <svg height={120} width={120}>
        <circle
          stroke="#eee"
          fill="transparent"
          strokeWidth={stroke}
          r={normalized}
          cx="60"
          cy="60"
        />

        <circle
          stroke={color}
          fill="transparent"
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          r={normalized}
          cx="60"
          cy="60"
          style={{ transition: "stroke-dashoffset 0.3s" }}
        />

        {status === "uploading" && (
          <text
            x="50%"
            y="50%"
            dominantBaseline="middle"
            textAnchor="middle"
            fontSize="18"
          >
            {progress}%
          </text>
        )}

        {status === "success" && (
          <image href={Check} x="35" y="35" height="50" width="50" />
        )}

        {status === "error" && (
          <image href={Remove} x="35" y="35" height="50" width="50" />
        )}
      </svg>
    </div>
  );
}